/**
 * QR-Code-Kodierer nach ISO/IEC 18004 in reinem TypeScript (keine Abhängigkeit, kein Netzwerk). Gebraucht für den
 * Zahlteil der Schweizer QR-Rechnung (swiss-qr-bill.ts), die Byte-Modus und Fehlerkorrektur M vorschreibt und höchstens
 * Version 25 erlaubt; der Kodierer selbst kann alle vier Stufen und die Versionen 1 bis 40.
 *
 * Ablauf: Daten im Byte-Modus (Modusanzeige 0100, Zeichenzahl mit 8 bzw. 16 Bit, Endkennung, Füllbytes 0xEC/0x11) →
 * Aufteilung in Blöcke mit Reed-Solomon-Prüfwörtern (GF(256), Polynom 0x11D) → Verschränkung → Funktionsmuster
 * (Finder, Separatoren, Taktlinien, Ausrichtungsmuster, dunkles Modul, Format- und Versionsinformation) → Einlegen im
 * Zickzack → alle acht Masken bewerten (Strafpunkte N1 bis N4) und die beste nehmen.
 *
 * Ergebnis ist eine Matrix aus Modulen (1 = dunkel); wie sie gezeichnet wird (PDF, SVG, Canvas), entscheidet der Aufrufer.
 */

export type QrEcc = "L" | "M" | "Q" | "H";

export interface QrCode {
  version: number;
  /** Kantenlänge in Modulen (17 + 4 × Version), ohne Ruhezone */
  size: number;
  ecc: QrEcc;
  /** gewählte Maske 0–7 */
  mask: number;
  /** size × size Module zeilenweise, 1 = dunkel */
  modules: Uint8Array;
}

/** Modul an Spalte x, Zeile y dunkel? */
export function qrModule(qr: QrCode, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < qr.size && y < qr.size && qr.modules[y * qr.size + x] === 1;
}

// ---------------------------------------------------------------------------
// Tabellen (ISO/IEC 18004, Tabelle 9): Prüfwörter je Block und Zahl der Blöcke je Version und Stufe; Index 0 unbenutzt
// ---------------------------------------------------------------------------

const ECC_CODEWORDS_PER_BLOCK: Record<QrEcc, readonly number[]> = {
  L: [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  M: [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
  Q: [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  H: [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
};
const NUM_ERROR_CORRECTION_BLOCKS: Record<QrEcc, readonly number[]> = {
  L: [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  M: [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
  Q: [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
  H: [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
};
/** Bits der Fehlerkorrekturstufe in der Formatinformation */
const ECC_FORMAT_BITS: Record<QrEcc, number> = { L: 1, M: 0, Q: 3, H: 2 };

export const QR_MIN_VERSION = 1;
export const QR_MAX_VERSION = 40;

/** Module für Daten und Prüfwörter (ohne Funktionsmuster und Format-/Versionsinformation) */
export function qrRawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (version >= 7) result -= 36;
  }
  return result;
}

/** Datenwörter (Bytes) einer Version und Stufe ohne Prüfwörter */
export function qrDataCodewords(version: number, ecc: QrEcc): number {
  return Math.floor(qrRawDataModules(version) / 8) - ECC_CODEWORDS_PER_BLOCK[ecc][version]! * NUM_ERROR_CORRECTION_BLOCKS[ecc][version]!;
}

/** Wie viele Bytes passen im Byte-Modus in eine Version und Stufe (Kopf 4 + 8 bzw. 16 Bit abgezogen) */
export function qrByteCapacity(version: number, ecc: QrEcc): number {
  const headerBits = 4 + (version <= 9 ? 8 : 16);
  return Math.floor((qrDataCodewords(version, ecc) * 8 - headerBits) / 8);
}

/** Mittelpunkte der Ausrichtungsmuster (Zeilen und Spalten) einer Version */
export function qrAlignmentPositions(version: number): number[] {
  if (version === 1) return [];
  const numAlign = Math.floor(version / 7) + 2;
  const size = version * 4 + 17;
  const step = Math.floor((version * 8 + numAlign * 3 + 5) / (numAlign * 4 - 4)) * 2;
  const result = [6];
  for (let pos = size - 7; result.length < numAlign; pos -= step) result.splice(1, 0, pos);
  return result;
}

// ---------------------------------------------------------------------------
// Reed-Solomon über GF(256), Polynom x^8 + x^4 + x^3 + x^2 + 1 (0x11D)
// ---------------------------------------------------------------------------

function gfMultiply(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

/** Erzeugerpolynom vom Grad n (führende 1 weggelassen), Koeffizienten vom höchsten zum niedrigsten Grad */
export function qrRsDivisor(degree: number): number[] {
  const result = new Array<number>(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = gfMultiply(result[j]!, root);
      if (j + 1 < degree) result[j] = result[j]! ^ result[j + 1]!;
    }
    root = gfMultiply(root, 0x02);
  }
  return result;
}

/** Prüfwörter zu einem Datenblock */
export function qrRsRemainder(data: readonly number[], divisor: readonly number[]): number[] {
  const result = new Array<number>(divisor.length).fill(0);
  for (const b of data) {
    const factor = b ^ result.shift()!;
    result.push(0);
    divisor.forEach((coef, i) => { result[i] = result[i]! ^ gfMultiply(coef, factor); });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Format- und Versionsinformation (BCH-Codes)
// ---------------------------------------------------------------------------

/** 15 Bit Formatinformation (Stufe + Maske, BCH(15,5), mit 0x5412 maskiert) */
export function qrFormatBits(ecc: QrEcc, mask: number): number {
  const data = (ECC_FORMAT_BITS[ecc] << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  return ((data << 10) | rem) ^ 0x5412;
}

/** 18 Bit Versionsinformation ab Version 7 (BCH(18,6), Polynom 0x1F25) */
export function qrVersionBits(version: number): number {
  let rem = version;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  return (version << 12) | rem;
}

// ---------------------------------------------------------------------------
// Kodieren
// ---------------------------------------------------------------------------

/** UTF-8 ohne TextEncoder-Abhängigkeit (Deno, Browser, Node) */
export function utf8Bytes(text: string): Uint8Array {
  const out: number[] = [];
  for (const ch of text) {
    let cp = ch.codePointAt(0)!;
    if (cp >= 0xd800 && cp <= 0xdfff) cp = 0xfffd;
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 63));
    else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    else out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 63), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
  }
  return new Uint8Array(out);
}

export class QrTooLongError extends Error {
  constructor(readonly bytes: number, readonly maxVersion: number, readonly ecc: QrEcc) {
    super(`QR-Code: ${bytes} Bytes passen nicht in Version ${maxVersion} (Stufe ${ecc}).`);
  }
}

export interface QrOptions {
  /** Fehlerkorrektur; Standard M */
  ecc?: QrEcc;
  minVersion?: number;
  maxVersion?: number;
  /** feste Maske 0–7 statt der besten (für Tests) */
  mask?: number;
}

/** Text (UTF-8) bzw. Bytes als QR-Code im Byte-Modus */
export function encodeQr(input: string | Uint8Array, opts: QrOptions = {}): QrCode {
  const bytes = typeof input === "string" ? utf8Bytes(input) : input;
  const ecc = opts.ecc ?? "M";
  const minV = Math.max(QR_MIN_VERSION, opts.minVersion ?? QR_MIN_VERSION);
  const maxV = Math.min(QR_MAX_VERSION, opts.maxVersion ?? QR_MAX_VERSION);
  let version = minV;
  while (version <= maxV && qrByteCapacity(version, ecc) < bytes.length) version++;
  if (version > maxV) throw new QrTooLongError(bytes.length, maxV, ecc);

  // Bitstrom: Modus 0100, Zeichenzahl, Daten, Endkennung, Auffüllen auf Bytes, Füllbytes
  const bits: number[] = [];
  const put = (value: number, len: number) => { for (let i = len - 1; i >= 0; i--) bits.push((value >>> i) & 1); };
  put(0b0100, 4);
  put(bytes.length, version <= 9 ? 8 : 16);
  for (const b of bytes) put(b, 8);
  const capacityBits = qrDataCodewords(version, ecc) * 8;
  put(0, Math.min(4, capacityBits - bits.length));
  put(0, (8 - (bits.length % 8)) % 8);
  for (let pad = 0xec; bits.length < capacityBits; pad ^= 0xec ^ 0x11) put(pad, 8);
  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) data.push(bits.slice(i, i + 8).reduce((a, b) => (a << 1) | b, 0));

  const codewords = addEccAndInterleave(data, version, ecc);
  const m = new Matrix(version);
  m.drawFunctionPatterns(ecc);
  m.drawCodewords(codewords);
  let mask = opts.mask ?? -1;
  if (mask < 0) {
    let best = Infinity;
    for (let k = 0; k < 8; k++) {
      m.applyMask(k);
      m.drawFormatBits(ecc, k);
      const penalty = m.penalty();
      if (penalty < best) { best = penalty; mask = k; }
      m.applyMask(k); // Maske ist ihre eigene Umkehrung
    }
  }
  m.applyMask(mask);
  m.drawFormatBits(ecc, mask);
  return { version, size: m.size, ecc, mask, modules: m.modules };
}

/** Datenwörter in Blöcke teilen, Prüfwörter anhängen und verschränken */
export function addEccAndInterleave(data: readonly number[], version: number, ecc: QrEcc): number[] {
  const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[ecc][version]!;
  const blockEccLen = ECC_CODEWORDS_PER_BLOCK[ecc][version]!;
  const rawCodewords = Math.floor(qrRawDataModules(version) / 8);
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortBlockLen = Math.floor(rawCodewords / numBlocks);
  const divisor = qrRsDivisor(blockEccLen);
  const blocks: number[][] = [];
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const dat = data.slice(k, k + shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1));
    k += dat.length;
    const eccWords = qrRsRemainder(dat, divisor);
    if (i < numShortBlocks) dat.push(0);
    blocks.push([...dat, ...eccWords]);
  }
  const result: number[] = [];
  for (let i = 0; i < blocks[0]!.length; i++) {
    blocks.forEach((block, j) => {
      if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) result.push(block[i]!);
    });
  }
  return result;
}

const PENALTY_N1 = 3, PENALTY_N2 = 3, PENALTY_N3 = 40, PENALTY_N4 = 10;

class Matrix {
  readonly size: number;
  readonly modules: Uint8Array;
  private readonly isFunction: Uint8Array;
  constructor(readonly version: number) {
    this.size = version * 4 + 17;
    this.modules = new Uint8Array(this.size * this.size);
    this.isFunction = new Uint8Array(this.size * this.size);
  }
  private get(x: number, y: number): boolean { return this.modules[y * this.size + x] === 1; }
  private setFunction(x: number, y: number, dark: boolean) {
    this.modules[y * this.size + x] = dark ? 1 : 0;
    this.isFunction[y * this.size + x] = 1;
  }

  drawFunctionPatterns(ecc: QrEcc) {
    const s = this.size;
    for (let i = 0; i < s; i++) {
      this.setFunction(6, i, i % 2 === 0);
      this.setFunction(i, 6, i % 2 === 0);
    }
    this.finder(3, 3);
    this.finder(s - 4, 3);
    this.finder(3, s - 4);
    const pos = qrAlignmentPositions(this.version);
    const n = pos.length;
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if ((i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0)) continue;
        this.alignment(pos[i]!, pos[j]!);
      }
    }
    this.drawFormatBits(ecc, 0); // Platzhalter, damit die Felder als Funktionsmodule gelten
    this.drawVersion();
  }

  private finder(x: number, y: number) {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        const xx = x + dx, yy = y + dy;
        if (xx >= 0 && xx < this.size && yy >= 0 && yy < this.size) this.setFunction(xx, yy, dist !== 2 && dist !== 4);
      }
    }
  }

  private alignment(x: number, y: number) {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) this.setFunction(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  }

  drawFormatBits(ecc: QrEcc, mask: number) {
    const bits = qrFormatBits(ecc, mask);
    const bit = (i: number) => ((bits >>> i) & 1) === 1;
    const s = this.size;
    for (let i = 0; i <= 5; i++) this.setFunction(8, i, bit(i));
    this.setFunction(8, 7, bit(6));
    this.setFunction(8, 8, bit(7));
    this.setFunction(7, 8, bit(8));
    for (let i = 9; i < 15; i++) this.setFunction(14 - i, 8, bit(i));
    for (let i = 0; i < 8; i++) this.setFunction(s - 1 - i, 8, bit(i));
    for (let i = 8; i < 15; i++) this.setFunction(8, s - 15 + i, bit(i));
    this.setFunction(8, s - 8, true); // dunkles Modul
  }

  private drawVersion() {
    if (this.version < 7) return;
    const bits = qrVersionBits(this.version);
    for (let i = 0; i < 18; i++) {
      const dark = ((bits >>> i) & 1) === 1;
      const a = this.size - 11 + (i % 3), b = Math.floor(i / 3);
      this.setFunction(a, b, dark);
      this.setFunction(b, a, dark);
    }
  }

  drawCodewords(data: readonly number[]) {
    const s = this.size;
    let i = 0;
    for (let right = s - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (let vert = 0; vert < s; vert++) {
        for (let j = 0; j < 2; j++) {
          const x = right - j;
          const upward = ((right + 1) & 2) === 0;
          const y = upward ? s - 1 - vert : vert;
          if (!this.isFunction[y * s + x] && i < data.length * 8) {
            this.modules[y * s + x] = (data[i >>> 3]! >>> (7 - (i & 7))) & 1;
            i++;
          }
        }
      }
    }
  }

  applyMask(mask: number) {
    const s = this.size;
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        if (this.isFunction[y * s + x]) continue;
        let invert: boolean;
        switch (mask) {
          case 0: invert = (x + y) % 2 === 0; break;
          case 1: invert = y % 2 === 0; break;
          case 2: invert = x % 3 === 0; break;
          case 3: invert = (x + y) % 3 === 0; break;
          case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
          case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
          case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          case 7: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          default: throw new Error("Maske 0–7");
        }
        if (invert) this.modules[y * s + x] = this.modules[y * s + x]! ^ 1;
      }
    }
  }

  /** Strafpunkte nach ISO/IEC 18004 7.8.3 (N1 Läufe, N2 Blöcke, N3 Finder-ähnliche Muster, N4 Verhältnis hell/dunkel) */
  penalty(): number {
    const s = this.size;
    let result = 0;
    const lines = (get: (a: number, b: number) => boolean) => {
      for (let a = 0; a < s; a++) {
        let runColor = false, run = 0;
        const history = [0, 0, 0, 0, 0, 0, 0];
        for (let b = 0; b < s; b++) {
          if (get(a, b) === runColor) {
            run++;
            if (run === 5) result += PENALTY_N1;
            else if (run > 5) result++;
          } else {
            this.addHistory(run, history);
            if (!runColor) result += this.countPatterns(history) * PENALTY_N3;
            runColor = get(a, b);
            run = 1;
          }
        }
        result += this.terminateAndCount(runColor, run, history) * PENALTY_N3;
      }
    };
    lines((y, x) => this.get(x, y));
    lines((x, y) => this.get(x, y));
    for (let y = 0; y < s - 1; y++) {
      for (let x = 0; x < s - 1; x++) {
        const c = this.get(x, y);
        if (c === this.get(x + 1, y) && c === this.get(x, y + 1) && c === this.get(x + 1, y + 1)) result += PENALTY_N2;
      }
    }
    let dark = 0;
    for (const v of this.modules) dark += v;
    const total = s * s;
    const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
    result += k * PENALTY_N4;
    return result;
  }

  private countPatterns(h: number[]): number {
    const n = h[1]!;
    const core = n > 0 && h[2] === n && h[3] === n * 3 && h[4] === n && h[5] === n;
    return (core && h[0]! >= n * 4 && h[6]! >= n ? 1 : 0) + (core && h[6]! >= n * 4 && h[0]! >= n ? 1 : 0);
  }
  private terminateAndCount(runColor: boolean, run: number, h: number[]): number {
    if (runColor) {
      this.addHistory(run, h);
      run = 0;
    }
    run += this.size; // helle Ruhezone am Ende
    this.addHistory(run, h);
    return this.countPatterns(h);
  }
  private addHistory(run: number, h: number[]) {
    if (h[0] === 0) run += this.size; // helle Ruhezone am Anfang
    h.pop();
    h.unshift(run);
  }
}

/** Dunkle Module als Rechtecke, je Zeile zusammengefasste Läufe (für PDF und SVG): [x, y, Breite] in Modulen */
export function qrRuns(qr: QrCode): [number, number, number][] {
  const out: [number, number, number][] = [];
  for (let y = 0; y < qr.size; y++) {
    let x = 0;
    while (x < qr.size) {
      if (!qrModule(qr, x, y)) { x++; continue; }
      const start = x;
      while (x < qr.size && qrModule(qr, x, y)) x++;
      out.push([start, y, x - start]);
    }
  }
  return out;
}

/** SVG-Pfad der dunklen Module (Einheit = ein Modul, ohne Ruhezone) */
export function qrSvgPath(qr: QrCode): string {
  return qrRuns(qr).map(([x, y, w]) => `M${x} ${y}h${w}v1h-${w}z`).join("");
}
